import { ChildTag } from '@components/child-tag/child-tag';
import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { LinkButton } from '@eika/button';
import { DetailsListItem } from '@eika/details-list';
import { formatPhoneNumber } from '@eika-infrastruktur/format';
import { useAccountApplication } from '@hooks/context/use-account-application-context';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import { getApproverParentName } from '@utils/child-eligibility-utils';
import { formatDateString } from '@utils/date-utils';
import { yearsOld } from '@utils/process-utils';
import { type ReactNode, useState } from 'react';
import { PARTIAL_TAKEOVER_AGE } from '@/constants/partial-takeover-age';
import { AboutAccountForChildModal } from './about-account-for-child-modal/about-account-for-child-modal';

interface AccountOwnerProps {
    name: string;
}

const AccountOwner = ({ name }: AccountOwnerProps) => {
    const { processSummary } = useTranslation();

    return (
        <DetailsListItem
            className="process-details__list__names"
            label={processSummary.accountOwner}
            value={name}
            formatter={(name) => <ChildTag name={name} />}
            data-testid="account-owner"
        />
    );
};

interface ParentsProps {
    applicantName: string;
    approverName: string;
}

const Parents = ({ applicantName, approverName }: ParentsProps) => {
    const { processSummary } = useTranslation();

    return (
        <DetailsListItem
            label={processSummary.parents}
            value={{ applicantName, approverName }}
            className="process-details__list__names"
            formatter={({ applicantName, approverName }) => (
                <ul className="process-details__list__names--parents">
                    <li>{applicantName}</li>
                    <li>{approverName}</li>
                </ul>
            )}
        />
    );
};

interface ParentEmailProps {
    email: string | undefined;
}

const ParentEmail = ({ email }: ParentEmailProps) => {
    const { pendingProcess } = useTranslation();

    return (
        <DetailsListItem
            label={pendingProcess.details.email}
            placeholder={pendingProcess.details.emailIsMissing}
            value={email}
        />
    );
};

interface ApproverParentPhoneNumberProps {
    name: string;
    phoneNumber: string;
}

const ApproverParentPhoneNumber = ({ name, phoneNumber }: ApproverParentPhoneNumberProps) => {
    const { pendingProcess } = useTranslation();

    return (
        <DetailsListItem
            label={
                <>
                    <ScreenReaderElement text={`${pendingProcess.details.smsHasBeenSentTo} ${name}`} />
                    <span aria-hidden="true" data-testid="approver-parent-phone-number-name">
                        {pendingProcess.details.smsHasBeenSentTo}
                        <br />
                        {`(${name})`}
                    </span>
                </>
            }
            value={formatPhoneNumber(phoneNumber)}
        />
    );
};

const NotifyApproverParentOnPhoneNumber = () => {
    const { pendingProcess } = useTranslation();
    const { notifyApproverParentOnPhoneNumber, selectedChildEligibility } = useAccountApplication();

    const value =
        selectedChildEligibility && notifyApproverParentOnPhoneNumber
            ? formatPhoneNumber(formatPhoneNumber(notifyApproverParentOnPhoneNumber))
            : undefined;

    const name = selectedChildEligibility ? getApproverParentName(selectedChildEligibility) : '';

    return (
        <DetailsListItem
            label={
                <>
                    <ScreenReaderElement text={`${pendingProcess.details.mobileNrTo} ${name}`} />
                    <span aria-hidden="true">
                        {pendingProcess.details.mobileNrTo}
                        <br />
                        {format(pendingProcess.details.approverParentPhoneNumber, name)}
                    </span>
                </>
            }
            value={value}
            placeholder={pendingProcess.details.mobileIsMissing}
        />
    );
};

interface CreatedDateProps {
    date: string;
}

const CreatedDate = ({ date }: CreatedDateProps) => {
    const { pendingProcess } = useTranslation();

    return <DetailsListItem label={pendingProcess.details.createdDate} value={formatDateString(date)} />;
};

const AboutAsk = () => {
    const { pendingProcess } = useTranslation();
    const [showAboutAccountForChildModal, setShowAboutAccountForChildModal] = useState<boolean>(false);

    return (
        <>
            <DetailsListItem
                label={pendingProcess.details.aboutAskLabel}
                value={pendingProcess.details.aboutAskLinkText}
                formatter={(value) => (
                    <LinkButton
                        onClick={() => setShowAboutAccountForChildModal(true)}
                        className="process-details__about-ask-link"
                        data-testid="about-ask-link"
                    >
                        {value}
                    </LinkButton>
                )}
            />
            <AboutAccountForChildModal
                open={showAboutAccountForChildModal}
                setOpen={setShowAboutAccountForChildModal}
            />
        </>
    );
};

interface HandoverAgeProps {
    handoverAge: number;
}

const HandoverAge = ({ handoverAge }: HandoverAgeProps) => {
    const { pendingProcess } = useTranslation();

    return <DetailsListItem label={pendingProcess.details.handoverAge} value={yearsOld(handoverAge)} />;
};

const PartialAccessAge = () => {
    const { pendingProcess } = useTranslation();

    return <DetailsListItem label={pendingProcess.details.partialAccessAge} value={yearsOld(PARTIAL_TAKEOVER_AGE)} />;
};

interface DetailItemProps {
    children: ReactNode;
}

export const DetailItem = ({ children }: DetailItemProps) => children;

DetailItem.AccountOwner = AccountOwner;
DetailItem.Parents = Parents;
DetailItem.ParentEmail = ParentEmail;
DetailItem.ApproverParentPhoneNumber = ApproverParentPhoneNumber;
DetailItem.NotifyApproverParentOnPhoneNumber = NotifyApproverParentOnPhoneNumber;
DetailItem.HandoverAge = HandoverAge;
DetailItem.PartialAccessAge = PartialAccessAge;
DetailItem.AboutAsk = AboutAsk;
DetailItem.CreatedDate = CreatedDate;
